import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Building, Globe } from 'react-bootstrap-icons';
import { useAuth0 } from '@auth0/auth0-react';
import { getSignupApiUrl } from '../services/api';
import { auth0Config } from '../config/auth0';
import { useAccount } from '../contexts/AccountContext';

const _SIGNUP_API_BASE = process.env.REACT_APP_SIGNUP_API_URL;

function getSignupBase() {
  return (_SIGNUP_API_BASE || '').trim().replace(/\/+$/, '') || getSignupApiUrl();
}

/**
 * Server-authoritative onboarding/tenant gate.
 *
 * Calls /signup/status on mount to discover the user's real memberships, then
 * pushes them to AccountContext via bootstrapServerTenantState. Blocks rendering
 * children until the bootstrap has completed, so no tenant-guarded query ever
 * fires with a stale localStorage tenant or no tenant at all.
 *
 * If the user needs first-time onboarding, shows the company setup modal and
 * blurs out the rest of the app.
 */
export default function CompanyTenantSetupGate({ children }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { bootstrapServerTenantState } = useAccount();
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  const getAuthHeaders = useCallback(async () => {
    const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
    if (isAuthenticated) {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: auth0Config.audience },
        });
        if (token) headers['Authorization'] = `Bearer ${token}`;
      } catch (tokenErr) {
        console.warn('CompanyTenantSetupGate: token error', tokenErr?.message);
      }
    }
    return headers;
  }, [isAuthenticated, getAccessTokenSilently]);

  const loadStatus = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    setError(null);
    try {
      const url = `${getSignupBase()}/signup/status`;
      const headers = await getAuthHeaders();

      console.log('CompanyTenantSetupGate: fetching', url, 'auth:', !!headers['Authorization']);
      const res = await fetch(url, { headers });
      console.log('CompanyTenantSetupGate: response', res.status);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text}`);
      }
      const json = await res.json();
      const data = (json?.success && json?.data !== undefined) ? json.data : (json?.data ?? json);
      setStatus(data);

      // Push server-authoritative tenant state to AccountContext (atomic).
      // Skips the bootstrap when onboarding is required: the user has no real
      // memberships yet and the modal will write the resolved tenant to
      // localStorage when they complete setup, then reload.
      const needsOnboarding = data?.needsCompanyOnboarding === true;
      if (!needsOnboarding) {
        const tenants = Array.isArray(data?.tenants) ? data.tenants : [];
        const fromApi = String(data?.defaultTenantId || '').trim();
        const defaultId =
          (fromApi && tenants.some((t) => String(t?.tenantId || '') === fromApi) ? fromApi : '')
          || (tenants.length ? String(tenants[0]?.tenantId || '').trim() : '');
        console.log('[TENANT-TRACE] gate: server tenants:', {
          tenants: tenants.map((t) => t?.tenantId),
          defaultId,
          state: data?.state,
        });
        try {
          bootstrapServerTenantState({ tenants, defaultTenantId: defaultId });
        } catch (bootErr) {
          console.error('CompanyTenantSetupGate: bootstrap failed', bootErr);
        }
      }
    } catch (e) {
      console.error('CompanyTenantSetupGate: fetch failed', e);
      setIsError(true);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders, bootstrapServerTenantState]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const refetch = loadStatus;
  const [resolving, setResolving] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [localError, setLocalError] = useState('');

  const needs = status?.needsCompanyOnboarding === true;
  const state = status?.state;

  useEffect(() => {
    if (!needs) return;
    const keys = ['selectedCompanyTenantId', 'activeTenantId', 'selectedAccountId'];
    const before = {};
    keys.forEach((k) => { before[k] = localStorage.getItem(k); });
    console.log('[TENANT-TRACE] gate: onboarding required — clearing stale tenant localStorage:', before);
    keys.forEach((k) => localStorage.removeItem(k));
  }, [needs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    const name = String(companyName || '').trim();
    if (name.length < 2) {
      setLocalError('Please enter your company name (at least 2 characters).');
      return;
    }
    setResolving(true);
    try {
      const url = `${getSignupBase()}/signup/resolve-company`;
      const headers = await getAuthHeaders();
      const body = JSON.stringify({
        companyName: name,
        website: String(website || '').trim() || undefined,
      });

      console.log('CompanyTenantSetupGate: resolving', url);
      const res = await fetch(url, { method: 'POST', headers, body });
      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        const errMsg = json?.message
          || (typeof json?.error === 'string' ? json.error : null)
          || `HTTP ${res.status}`;
        throw new Error(errMsg);
      }

      const result = json?.data ?? json;

      if (result?.existingCompanyFound || result?.action === 'existing_company_found') {
        setLocalError(
          result?.message ||
            'An existing workspace was found for this domain. An administrator must invite you or approve your request.'
        );
        return;
      }

      if (result?.action === 'manual_review') {
        setLocalError(
          result?.message ||
            'We could not automatically set up your company. Please contact support.'
        );
        return;
      }

      console.log('[TENANT-TRACE] resolve-company response:', JSON.stringify(result));
      if (result?.tenantId) {
        try {
          localStorage.setItem('selectedCompanyTenantId', result.tenantId);
          localStorage.setItem('activeTenantId', result.tenantId);
          console.log('[TENANT-TRACE] localStorage SET:', {
            selectedCompanyTenantId: result.tenantId,
            activeTenantId: result.tenantId,
          });
        } catch (_) { /* ignore */ }
      } else {
        console.warn('[TENANT-TRACE] resolve-company: NO tenantId in response!', result);
      }
      console.log('[TENANT-TRACE] about to reload. localStorage snapshot:', {
        selectedCompanyTenantId: localStorage.getItem('selectedCompanyTenantId'),
        activeTenantId: localStorage.getItem('activeTenantId'),
        selectedAccountId: localStorage.getItem('selectedAccountId'),
      });
      window.location.reload();
    } catch (err) {
      const msg = err?.message || 'Request failed. Try again.';
      setLocalError(String(msg));
    } finally {
      setResolving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" variant="primary" />
          <div className="mt-2 text-muted">Loading workspace…</div>
        </div>
      </div>
    );
  }

  if (isError) {
    const msg = error?.message || 'Unknown error';

    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center" style={{ maxWidth: 480 }}>
          <Alert variant="warning" className="text-start">
            <strong>Unable to verify workspace status.</strong>
            <br />
            {msg}
          </Alert>
          <Button variant="primary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!needs) {
    if (state === 'pending_join') {
      return (
        <>
          <Alert variant="info" className="m-3 mb-0">
            Your request to join <strong>{status?.company?.companyName || 'your organization'}</strong>{' '}
            is pending approval from an administrator.
          </Alert>
          {children}
        </>
      );
    }
    return children;
  }

  return (
    <>
      <Modal show backdrop="static" keyboard={false} centered size="lg">
        <Modal.Header>
          <Modal.Title className="d-flex align-items-center gap-2">
            <Building size={22} />
            Set up your company workspace
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            We use your <strong>email domain</strong> (and optional company website) so new companies get
            their own workspace, while teammates on the same domain can request access instead of
            duplicating the organization.
          </p>
          {localError ? (
            <Alert variant="danger" className="py-2">
              {localError}
            </Alert>
          ) : null}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Company name</Form.Label>
              <Form.Control
                type="text"
                value={companyName}
                onChange={(ev) => setCompanyName(ev.target.value)}
                placeholder="e.g. Bright Path Roofing"
                autoComplete="organization"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Company website <span className="text-muted fw-normal">(optional)</span>
              </Form.Label>
              <div className="d-flex align-items-start gap-2">
                <Globe className="mt-2 text-muted" />
                <Form.Control
                  type="text"
                  value={website}
                  onChange={(ev) => setWebsite(ev.target.value)}
                  placeholder="https://yourcompany.com — recommended if you use Gmail/Yahoo for email"
                  autoComplete="url"
                />
              </div>
              <Form.Text className="text-muted">
                If you sign in with a personal email (Gmail, etc.), enter your business site so we can
                tie your workspace to your domain.
              </Form.Text>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button type="submit" variant="primary" disabled={resolving}>
                {resolving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving…
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <div style={{ filter: 'blur(4px)', pointerEvents: 'none', minHeight: '100vh' }} aria-hidden>
        {children}
      </div>
    </>
  );
}
